export const CHAT_CHART_TYPES = ['bar', 'line', 'donut', 'bridge'] as const;

export type ChatChartType = (typeof CHAT_CHART_TYPES)[number];

export type ChatChartSeries = {
  name: string;
  values: number[];
};

export type ChatChartRecipe = {
  type: ChatChartType;
  title?: string;
  labels: string[];
  values: number[];
  series?: ChatChartSeries[];
};

const MAX_POINTS = 48;
const MAX_SERIES = 8;
const MAX_LABEL_LENGTH = 80;
const MAX_TITLE_LENGTH = 120;

/** Content identity for memoizing Vega specs. Keep in sync with fields `createChatChartSpec` reads. */
export function chatChartRecipeKey(recipe: ChatChartRecipe): string {
  return JSON.stringify([
    recipe.type,
    recipe.title ?? '',
    recipe.labels,
    recipe.values,
    recipe.series ?? [],
  ]);
}

export function chatChartTypeFromFence(
  language: string | undefined
): ChatChartType | undefined {
  if (language == null || language === 'chart') {
    return undefined;
  }
  if (language === 'waterfall') {
    return 'bridge';
  }
  return isChatChartType(language) ? language : undefined;
}

export function isChatChartFence(language: string | undefined): boolean {
  return language === 'chart' || chatChartTypeFromFence(language) != null;
}

function toFiniteNumber(value: unknown): number | null {
  if (Array.isArray(value)) {
    return value.length === 1 ? toFiniteNumber(value[0]) : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value
    .replaceAll(/[\s,']/g, '')
    .replaceAll(/[€$£%]/g, '')
    .trim();
  if (normalized === '') {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function toLabel(value: unknown): string | null {
  const raw =
    typeof value === 'number' && Number.isFinite(value) ? String(value) : value;
  if (typeof raw !== 'string') {
    return null;
  }

  const label = raw.trim();
  if (label === '' || label.length > MAX_LABEL_LENGTH) {
    return null;
  }

  return label;
}

function isChatChartType(value: unknown): value is ChatChartType {
  return (
    typeof value === 'string' &&
    (CHAT_CHART_TYPES as readonly string[]).includes(value)
  );
}

function chartTypeFromUnknown(value: unknown): ChatChartType | undefined {
  if (value === 'waterfall') {
    return 'bridge';
  }
  return isChatChartType(value) ? value : undefined;
}

function asNumberArray(value: unknown): unknown[] | null {
  if (Array.isArray(value)) {
    return value;
  }
  return null;
}

function tryParseJson(source: string): unknown {
  try {
    return JSON.parse(source) as unknown;
  } catch {
    return null;
  }
}

function repairAdjacentArrays(source: string): string {
  return source.replaceAll(/\]\]\s*\[\[/g, '],[');
}

function parseJsonObject(source: string): unknown {
  const candidates = [source];
  const repaired = repairAdjacentArrays(source);
  if (repaired !== source) {
    candidates.push(repaired);
  }

  for (const candidate of candidates) {
    const parsed = tryParseJson(candidate);
    if (parsed != null) {
      return parsed;
    }

    const start = candidate.indexOf('{');
    const end = candidate.lastIndexOf('}');
    if (start === -1 || end <= start) {
      continue;
    }

    const sliced = tryParseJson(candidate.slice(start, end + 1));
    if (sliced != null) {
      return sliced;
    }
  }

  return null;
}

export function parseChatChartRecipe(
  source: string,
  fallbackType?: ChatChartType
): ChatChartRecipe | null {
  const parsed = parseJsonObject(source);
  if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }

  const record = parsed as Record<string, unknown>;
  const type = chartTypeFromUnknown(record.type) ?? fallbackType;
  if (type == null) {
    return null;
  }

  const rawLabels = record.labels;
  if (!Array.isArray(rawLabels)) {
    return null;
  }

  const title =
    typeof record.title === 'string' &&
    record.title.trim() !== '' &&
    record.title.length <= MAX_TITLE_LENGTH
      ? record.title
      : undefined;

  const { labels, indexes } = collectValidLabels(rawLabels);
  if (labels.length === 0) {
    return null;
  }

  if (type === 'line') {
    const series = uniquifySeriesNames(parseLineSeries(record, indexes));
    if (series.length > 1) {
      return {
        type,
        ...(title ? { title } : {}),
        labels,
        values: series[0]?.values ?? [],
        series,
      };
    }
    if (series[0] != null) {
      return {
        type,
        ...(title ? { title } : {}),
        labels,
        values: series[0].values,
      };
    }
  }

  const rawValues = asNumberArray(record.values) ?? asNumberArray(record.value);
  if (rawValues == null) {
    return null;
  }

  const zippedLabels: string[] = [];
  const zippedValues: number[] = [];
  const count = Math.min(rawLabels.length, rawValues.length, MAX_POINTS);
  for (let index = 0; index < count; index += 1) {
    const label = toLabel(rawLabels[index]);
    const value = toFiniteNumber(rawValues[index]);
    if (label == null || value == null) {
      continue;
    }
    zippedLabels.push(label);
    zippedValues.push(value);
  }

  if (zippedLabels.length === 0) {
    return null;
  }

  return {
    type,
    ...(title ? { title } : {}),
    labels: zippedLabels,
    values: zippedValues,
  };
}

function collectValidLabels(rawLabels: unknown[]): {
  indexes: number[];
  labels: string[];
} {
  const labels: string[] = [];
  const indexes: number[] = [];
  const count = Math.min(rawLabels.length, MAX_POINTS);
  for (let index = 0; index < count; index += 1) {
    const label = toLabel(rawLabels[index]);
    if (label == null) {
      continue;
    }
    labels.push(label);
    indexes.push(index);
  }

  return { indexes, labels };
}

function valuesAtIndexes(
  rawValues: unknown[] | null,
  indexes: number[]
): number[] | null {
  if (rawValues == null || indexes.length === 0) {
    return null;
  }

  const values: number[] = [];
  for (const index of indexes) {
    const value = toFiniteNumber(rawValues[index]);
    if (value == null) {
      return null;
    }
    values.push(value);
  }

  return values;
}

function parseNamedSeries(
  name: unknown,
  rawValues: unknown,
  indexes: number[]
): ChatChartSeries | null {
  const seriesName = toLabel(name);
  const values = valuesAtIndexes(asNumberArray(rawValues), indexes);
  if (seriesName == null || values == null) {
    return null;
  }

  return { name: seriesName, values };
}

function parseLineSeries(
  record: Record<string, unknown>,
  indexes: number[]
): ChatChartSeries[] {
  const rawSeries = record.series;
  if (Array.isArray(rawSeries)) {
    const series: ChatChartSeries[] = [];
    for (const item of rawSeries.slice(0, MAX_SERIES)) {
      if (item != null && typeof item === 'object' && !Array.isArray(item)) {
        const entry = item as Record<string, unknown>;
        const parsed = parseNamedSeries(
          entry.name ?? entry.label ?? entry.title,
          entry.values ?? entry.value,
          indexes
        );
        if (parsed != null) {
          series.push(parsed);
        }
        continue;
      }

      if (typeof item === 'string') {
        const valuesSource =
          asNumberArray(record.values) ?? asNumberArray(record.value);
        const seriesIndex = series.length;
        const row = Array.isArray(valuesSource?.[seriesIndex])
          ? valuesSource[seriesIndex]
          : null;
        const parsed = parseNamedSeries(item, row, indexes);
        if (parsed != null) {
          series.push(parsed);
        }
      }
    }
    return series;
  }

  const rawValues = record.values ?? record.value;
  if (
    rawValues != null &&
    typeof rawValues === 'object' &&
    !Array.isArray(rawValues)
  ) {
    const series: ChatChartSeries[] = [];
    for (const [name, values] of Object.entries(
      rawValues as Record<string, unknown>
    )) {
      if (series.length >= MAX_SERIES) {
        break;
      }
      const parsed = parseNamedSeries(name, values, indexes);
      if (parsed != null) {
        series.push(parsed);
      }
    }
    return series;
  }

  return [];
}

function uniquifySeriesNames(series: ChatChartSeries[]): ChatChartSeries[] {
  const seen = new Map<string, number>();
  return series.map((item) => {
    const count = (seen.get(item.name) ?? 0) + 1;
    seen.set(item.name, count);
    return count === 1 ? item : { ...item, name: `${item.name} (${count})` };
  });
}
